import React, { useState, useRef, useLayoutEffect } from 'react';
import classNames from 'classnames';

// Components
import StyledPopupInputContainer from '../../../../../../components/form/fields/StyledPopupInputContainer';
import StyledButton from '../../../../../../components/buttons/StyledButton';
import Checkbox from '../../../../../../components/editor/plugins/checklist/components/Checkbox';

// Utils
import http from '../../../../../../utils/services/http';

// Constants
import { AI_FEEDBACK_OPTIONS } from '../../../../../../../common/ai/aiModerationConstants';

// Types
import { AiDebugInfo } from '../../../../../../../common/ai/aiConversationTypes';

// Styles
import './AiAssistantReportFeedbackModalContent.scss';

interface ReportFeedbackModalContentProps {
    conversationId: string;
    title: string;
    debugInfo: AiDebugInfo;
    dispatchModalClose: () => void;
}

const MAX_CHARS = 600;

const AiAssistantReportFeedbackModalContent = ({
    conversationId,
    title,
    debugInfo,
    dispatchModalClose,
}: ReportFeedbackModalContentProps): React.ReactElement => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [selectedFeedbackOption, setSelectedFeedbackOptions] = useState<string[]>([]);
    const [showSuccessStep, setShowSuccessStep] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const disableSubmitButton =
        (selectedFeedbackOption.length < 1 && feedbackMessage.trim().length < 1) || isSubmitting;

    useLayoutEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.focus();
        }
    });

    const onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFeedbackMessage(event.target.value);
    };

    const closeModal = () => dispatchModalClose();

    const onOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        if (checked) {
            setSelectedFeedbackOptions([...selectedFeedbackOption, event.target.value]);
        } else {
            setSelectedFeedbackOptions(selectedFeedbackOption.filter((option) => option !== event.target.value));
        }
    };

    const onSubmit = async (): Promise<void> => {
        setHasError(false);
        setIsSubmitting(true);

        try {
            await http({
                url: `ai/conversations/feedback`,
                method: 'POST',
                data: {
                    conversationId,
                    title,
                    feedbackText: feedbackMessage,
                    feedbackCategories: selectedFeedbackOption,
                    debugInfo,
                },
            });
            setIsSubmitting(false);
            setShowSuccessStep(true);
        } catch (e) {
            console.error(e);
            setIsSubmitting(false);
            setShowSuccessStep(false);
            setHasError(true);
        }
    };

    if (showSuccessStep) {
        return (
            <div className="AiAssistantReportFeedbackModalContent">
                <div className="feedback-success">
                    <h2>Your feedback has been submitted</h2>
                    <p>Thanks for helping us improve this feature!</p>
                    <StyledButton className="close-button primary" type="button" onClickFn={closeModal}>
                        Close
                    </StyledButton>
                </div>
            </div>
        );
    }

    return (
        <div className="AiAssistantReportFeedbackModalContent">
            <h2>What’s your feedback on this response from the AI?</h2>
            <p className="description-text">
                Describe what happened and let us know if you have suggestions for improvement
            </p>
            <StyledPopupInputContainer>
                <textarea
                    ref={textAreaRef}
                    className="feedback-text-area"
                    value={feedbackMessage}
                    onChange={onMessageChange}
                    maxLength={MAX_CHARS}
                />
            </StyledPopupInputContainer>
            <p className="options-list-title"> Do any of these apply?</p>
            <ul className="options-list">
                {AI_FEEDBACK_OPTIONS.map((option, index) => (
                    <li key={index}>
                        <label
                            className={classNames('option-item', {
                                selected: selectedFeedbackOption.includes(option.id),
                            })}
                        >
                            <input
                                className="checkbox-input"
                                type="checkbox"
                                value={option.id}
                                checked={selectedFeedbackOption.includes(option.id)}
                                onChange={onOptionChange}
                            />

                            <Checkbox checked={selectedFeedbackOption.includes(option.id)} />

                            <span className="option-title">{option.title}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <span className="disclaimer-text">
                Please note: a copy of your current conversation will be included with your feedback
            </span>
            <StyledButton
                className="submit-button primary"
                type="button"
                disabled={disableSubmitButton}
                onClickFn={onSubmit}
            >
                Submit feedback
            </StyledButton>
            {hasError && <div className="error-message">Something went wrong</div>}
        </div>
    );
};

export default AiAssistantReportFeedbackModalContent;
